import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Landmark, Compass, ExternalLink } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const criteria = [
  "You are an Urban Indian not affiliated with any federal or state-recognized tribe.",
  "You possess confirmed Woodland Mississippian ancestry or Modern Freedmen lineage.",
  "You have completed genealogical research (censuses, lineage charts, family trees).",
  "You seek institutional recognition, permanence, and independent economic sovereignty.",
];

const faqs = [
  { q: "I have completed my genealogy but hold no tribal enrollment. Do I qualify?", a: "Yes. The Trust was created precisely for independent descendants with verified ancestry who fall outside federal or state tribal enrollment. Completed genealogical documentation is the foundation of eligibility." },
  { q: "What is a Modern Freedmen lineage pathway?", a: "The Modern Freedmen Bureau provides a dedicated pathway for descendants of Freedmen lineages — anchoring lineage, preserving cultural memory, and extending institutional recognition independent of external governmental bodies." },
  { q: "How does the Trust bridge the gap for unaffiliated descendants?", a: "Through a private institutional framework and a decentralized blockchain ancestry registry, the Trust records and recognizes verified lineage, then issues digital economic citizenship credentials — offering the permanence and standing that traditional frameworks deny independent descendants." },
  { q: "Does joining affect any existing status?", a: "No. The Trust operates as an independent, private institution. It does not replace or conflict with any recognition; it provides a sovereign-minded structure where none otherwise exists." },
];

export default function Eligibility({ embedded }) {
  return (
    <div>
      {!embedded && (
        <PageHeader
          eyebrow="Eligibility & Lineage"
          title="For the descendants who completed the work — and were left without a framework."
          subtitle="Woodland Mississippian & Modern Freedmen Heritage, recognized and anchored through the Modern Freedmen Bureau."
        />
      )}

      <section className="py-20 bg-ink-deep" data-testid="eligibility-map">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Ancestral Homelands</Eyebrow>
            <h2 className="font-serif font-light text-4xl tracking-tight mt-5 max-w-3xl">
              The Southeastern Woodlands.
            </h2>
            <p className="font-sans text-[#A19D98] leading-relaxed mt-6 max-w-3xl">
              Eligibility is rooted in the ancestral homelands of the Woodland Mississippian peoples — a vast
              cultural region spanning the Southeastern Woodlands, from the Atlantic coast across the Gulf and up
              the Mississippi, Ohio, and Tennessee river valleys.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <figure className="border border-gold/20 bg-ink-surface mt-10 overflow-hidden">
              <img
                src="/mississippian-map.jpg?v=3"
                alt="Academic historical map of the Southeastern Woodlands cultural region, ancestral homeland of the Woodland Mississippian peoples"
                data-testid="eligibility-map-image"
                className="w-full h-auto object-contain"
              />
              <figcaption className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/50 p-6 border-t border-gold/10">
                The Southeastern Woodlands cultural region · c. 800–1600 CE · Nations include Cherokee · Creek · Choctaw · Chickasaw · Natchez · Seminole · Timucua · Powhatan · Catawba · Caddo, and numerous confederations, chiefdoms, townships and coalescence
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-ink">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Eyebrow>Who We Serve</Eyebrow>
            <h2 className="font-serif font-light text-4xl tracking-tight mt-5">Eligibility Criteria</h2>
            <ul className="mt-10 space-y-6">
              {criteria.map((c) => (
                <li key={c} className="flex items-start gap-4">
                  <span className="flex items-center justify-center w-6 h-6 border border-gold/40 shrink-0 mt-0.5">
                    <Check size={13} className="text-gold" />
                  </span>
                  <span className="font-sans text-[15px] text-[#C9C4BD] leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="border border-gold/20 bg-ink-surface p-10 h-full">
              <Landmark size={30} className="text-gold" strokeWidth={1.2} />
              <h3 className="font-serif text-3xl mt-6">The Modern Freedmen Bureau</h3>
              <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-5">
                A dedicated institutional pathway for Modern Freedmen heritage preservation, lineage
                anchoring, and institutional recognition. The Bureau acts as an independent,
                technologically advanced registry and socio-economic anchor for descendants who possess
                verified ancestry but stand outside federal or state tribal frameworks.
              </p>
              <div className="gold-hairline my-8" />
              <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-gold/70 leading-relaxed">
                Independent Economic Sovereignty · Lineage Anchoring · Institutional Recognition
              </p>
              <Link to="/eligibility?tab=verify" data-testid="eligibility-verify-cta" className="group inline-flex items-center gap-3 mt-10 px-6 py-3 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors">
                Submit your lineage <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-ink" data-testid="eligibility-regalia">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Heritage & Regalia</Eyebrow>
            <h2 className="font-serif font-light text-4xl tracking-tight mt-5 max-w-3xl">
              The living material culture of the Southeastern Woodlands.
            </h2>
            <p className="font-sans text-[#A19D98] leading-relaxed mt-6 max-w-3xl">
              Men's and women's regalia — beadwork, ribbonwork, woven sashes, shell gorgets, and feathered
              adornment — carry the artistry and identity of the Southeastern Woodlands peoples across generations.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid md:grid-cols-2 gap-6 mt-10">
              <figure className="border border-gold/20 bg-ink-surface overflow-hidden">
                <img
                  src="/regalia-showcase.jpg?v=2"
                  alt="Museum exhibition showcase displaying men's and women's Southeastern Woodlands regalia adorned with turkey feathers"
                  data-testid="regalia-image"
                  className="w-full h-auto object-contain"
                />
                <figcaption className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/50 p-5 border-t border-gold/10">
                  Turkey-feather regalia · Men's &amp; women's ceremonial dress
                </figcaption>
              </figure>
              <figure className="border border-gold/20 bg-ink-surface overflow-hidden">
                <img
                  src="/regalia-beadwork.jpg"
                  alt="Museum exhibition showcase displaying men's and women's Southeastern Woodlands regalia with beadwork and ribbonwork"
                  data-testid="regalia-image-2"
                  className="w-full h-auto object-contain"
                />
                <figcaption className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/50 p-5 border-t border-gold/10">
                  Beadwork &amp; ribbonwork regalia · Men's &amp; women's traditional dress
                </figcaption>
              </figure>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-ink-deep">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Common Questions</Eyebrow>
            <h2 className="font-serif font-light text-4xl tracking-tight mt-5 mb-10">Bridging the gap for independent descendants</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="eligibility-faq">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-gold/15">
                  <AccordionTrigger className="font-sans text-left text-[15px] text-[#F5F0EB] hover:text-gold hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-sans text-sm text-[#A19D98] leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.15}>
            <div data-testid="eligibility-partner" className="border border-gold/25 bg-ink-surface p-8 sm:p-10 mt-12">
              <div className="flex items-start gap-4">
                <Compass size={26} className="text-gold shrink-0 mt-1" strokeWidth={1.3} />
                <div>
                  <Eyebrow>Need Help Tracing Your Lineage?</Eyebrow>
                  <h3 className="font-serif text-2xl mt-3">Search your heritage with our partner</h3>
                  <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">
                    If you have not yet completed your genealogical research, our trusted partner
                    <span className="text-gold-text"> Find Ya Tribe</span> can help you search your lineages,
                    trace your ancestry, and gather the documentation needed to establish eligibility with the Trust.
                  </p>
                  <a
                    href="https://findyatribe.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="eligibility-partner-link"
                    className="group inline-flex items-center gap-3 mt-6 font-mono text-[11px] tracking-[0.2em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors"
                  >
                    Visit findyatribe.org <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
